import React, { useMemo, useState } from "react";
import { Form, Formik } from "formik";
import * as yup from "yup";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Modal,
  Select,
  SxProps,
  TextField,
} from "@mui/material";
import { debounce } from "lodash";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { errorToastMessage } from "../../../utils/toast";
import { DrawerForm } from "../../Common/styles/drawer";
import { LabelStyle } from "../../Common/styles/form";
import {
  ModalHeader,
  ModalDrawerButtonStyles,
} from "../../Common/styles/modal";
import { useAppDispatch } from "../../../Redux/hooks";
import { handleMedicationUpdate } from "../../../Redux/reducers/responseSlice";
import { triggerSave } from "../../../Redux/actions/responseEventAction";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const routes = [
  "Buccal",
  "Inhalation",
  "Intramuscular",
  "Intravenous",
  "Nasal",
  "Ophthalmic",
  "Oral",
  "Rectal",
  "Subcutaneous",
  "Sublingual",
  "Transdermal",
  "Topical",
  "Vaginal",
];

const forms = [
  "Solution",
  "Gel",
  "Cream",
  "Tablet",
  "Drop",
  "Capsule",
  "Patch",
  "Spray",
  "Injection",
  "Suppository",
  "Inhaler",
];

const InputWrapper: SxProps = {
  width: "100%",
  justifyContent: "flex-start",
};

const ModalDrawerStyles: SxProps = {
  position: "absolute",
  left: {
    xs: "0%",
    md: "50%",
  },
  width: {
    xs: "100vw",
    md: "50vw",
  },
  bgcolor: "white",
  boxShadow: 4,
  p: "16px 24px 0 24px",
  outline: "none",
  height: "100%",
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
};

const schema = yup.object().shape({
  name: yup.string().required("TYPE_BASED.MEDICATION_NAME_REQUIRED"),
  code: yup.string().required("TYPE_BASED.MEDICATION_ATC_CODE_REQUIRED"),
  dose: yup.string().required("TYPE_BASED.MEDICATION_DOSE_REQUIRED"),
  route: yup.string().required("TYPE_BASED.MEDICATION_ROUTE_REQUIRED"),
  form: yup.string().required("TYPE_BASED.MEDICATION_FORM_REQUIRED"),
});

const MedicationModal: React.FC<any> = ({
  showModal,
  closeModal,
  data,
  questionId,
}: any) => {
  const { t } = useTranslation("responses");
  const { id: studyId, surveySlug } = useParams();
  const dispatch = useAppDispatch();
  const [buttonLoader, setButtonLoader] = useState(false);
  const [searchLoader, setSearchLoader] = useState(false);
  const [medications, setMedications] = useState<any>([]);

  const submitHandler = async (values: any) => {
    try {
      const body: any = {
        name: values.name,
        code: values.code,
        form: values.form,
        route: values.route,
        dose: values.dose,
      };
      dispatch(
        handleMedicationUpdate({
          id: questionId,
          medication: body,
          index: data?.index ?? undefined,
        })
      );
      dispatch(
        triggerSave({
          studyId,
          id: questionId,
          surveySlug,
        })
      );
      closeModal();
    } catch (err) {
      setButtonLoader(false);
      errorToastMessage(err as Error);
    }
  };

  const handleSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        try {
          if (value) {
            setSearchLoader(true);
            let url = `/data/drugs?q=${value}`;
            const res: AxiosResponse = await http.get(url);
            const meds = res.data.data.map((med: any) => {
              return {
                atcCode: med.atcCode,
                form: med.form,
                name: med.name,
                route: med.route,
                id: med.id,
              };
            });
            setMedications(meds);
            setSearchLoader(false);
          }
        } catch (err) {
          errorToastMessage(err as Error);
          setSearchLoader(false);
        }
      }, 500),
    []
  );

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader
          title={data ? t("TYPE_BASED.EDIT_MEDICATION") : t("TYPE_BASED.ADD_MEDICATION")}
          onCloseClick={closeModal}
        />
        <Formik
          initialValues={{
            name: data?.name || "",
            dose: data?.dose || "",
            form: data?.form || "",
            route: data?.route || "",
            code: data?.code || "",
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ errors, touched, getFieldProps, values, setFieldValue }: any) => (
            <Form style={DrawerForm}>
              <Box>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="name">
                    {t("TYPE_BASED.MEDICATION_NAME")} <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Autocomplete
                    filterOptions={(x) => x}
                    onInputChange={(_1: any, value: any, reason: string) => {
                      if (reason === "input") {
                        handleSearch(value);
                      }
                    }}
                    onChange={(_1: any, value: any) => {
                      if (value?.name) {
                        setFieldValue("name", value.name);
                        setFieldValue("code", value.atcCode);
                        setFieldValue("route", value.route);
                        setFieldValue("form", value.form);
                      } else {
                        setFieldValue("name", "");
                        setFieldValue("code", "");
                        setFieldValue("route", "");
                        setFieldValue("form", "");
                      }
                    }}
                    options={medications}
                    value={values?.name || null}
                    getOptionLabel={() => {
                      return values?.name || "";
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return option.name === value;
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name} ({option.route} - {option.form})
                      </li>
                    )}
                    loading={searchLoader}
                    loadingText={<CircularProgress size={20} />}
                    noOptionsText={t("TYPE_BASED.NO_RESULTS")}
                    clearOnBlur={true}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t("TYPE_BASED.SEARCH_MEDICATIONS")}
                        error={touched?.name && errors?.name ? true : false}
                      />
                    )}
                  />
                  <FormHelperText error>
                    {touched?.name && errors?.name
                      ? (errors?.name as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="atc-code">
                    {t("TYPE_BASED.MEDICATION_ATC_CODE")} <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    fullWidth
                    id="code"
                    value={values?.code}
                    error={touched?.code && errors?.code ? true : false}
                    helperText={
                      touched?.code && errors?.code
                        ? (errors?.code as string)
                        : " "
                    }
                    inputProps={{ readOnly: true }}
                    placeholder={t("TYPE_BASED.MEDICATION_ATC_CODE")}
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="route">
                    {t("TYPE_BASED.MEDICATION_ROUTE")} <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Select id="route" {...getFieldProps("route")}>
                    {routes.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error>
                    {touched?.route && errors?.route
                      ? (errors?.route as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="form">
                    {t("TYPE_BASED.MEDICATION_FORM")} <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Select id="form" {...getFieldProps("form")}>
                    {forms.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error>
                    {touched?.form && errors?.form
                      ? (errors?.form as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="dose">
                    {t("TYPE_BASED.MEDICATION_DOSE")} <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    fullWidth
                    id="dose"
                    {...getFieldProps("dose")}
                    error={touched?.dose && errors?.dose ? true : false}
                    helperText={
                      touched?.dose && errors?.dose
                        ? (errors?.dose as string)
                        : " "
                    }
                    placeholder={t("TYPE_BASED.MEDICATION_DOSE")}
                  />
                </FormControl>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <Divider />
                <Box sx={ModalDrawerButtonStyles}>
                  {!buttonLoader ? (
                    <>
                      <Button variant="contained" type="submit">
                        {t("SAVE", { ns: "common" })}
                      </Button>
                      <Button variant="outlined" onClick={closeModal}>
                        {t("CANCEL", { ns: "common" })}
                      </Button>
                    </>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default MedicationModal;
